import { httpUploadFile } from '@services/upload.http.ts'
import { type FC, useRef } from 'react'
import { useDropzone } from 'react-dropzone'
import toast from 'react-hot-toast'
import { Dropzone } from '@/components/ui/dropzone'
import {
	httpConfirmLuxeUpload,
	httpCreateLuxeGallery,
	httpRequestLuxeUploadPhotosLinks
} from '@/features/luxe-submissions/http/luxe-submissions.http'
import { cn, errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n.tsx'

interface ImageItem {
	galleryId: string
	presignedUrl: string
	imageId: string
	imageUrl: string
	uploading: boolean
	failed?: boolean
}
const MAX_FILES = 25

interface LuxePhotoUploadButtonProps {
	children: React.ReactNode
	className?: string
	galleryId: string
	photos: ImageItem[]
	onGalleryIdChange: (galleryId: string) => void
	onPhotosChange: (photos: ImageItem[]) => void
	setIsLoading: (isLoading: boolean) => void
}

export const LuxePhotoUploadButton: FC<LuxePhotoUploadButtonProps> = ({
	children,
	className,
	galleryId,
	photos,
	onGalleryIdChange,
	onPhotosChange,
	setIsLoading
}) => {
	const { t } = useTranslation()
	const isUploading = useRef(false)
	const photosRef = useRef(photos)
	photosRef.current = photos

	const ensureGallery = async (): Promise<string> => {
		if (galleryId) {
			return galleryId
		}

		const gallery = await httpCreateLuxeGallery()
		onGalleryIdChange(gallery.galleryId)
		return gallery.galleryId
	}

	const uploadFile = async (imageItem: ImageItem, file: File) => {
		try {
			await httpUploadFile({
				url: imageItem.presignedUrl,
				payload: file
			})
			await httpConfirmLuxeUpload({
				urlParams: {
					galleryId: imageItem.galleryId,
					imageId: imageItem.imageId
				}
			})
			const nextPhotos = photosRef.current.map((p) =>
				p.imageId === imageItem.imageId ? { ...p, uploading: false } : p
			)
			photosRef.current = nextPhotos
			onPhotosChange(nextPhotos)
		} catch (_error) {
			const nextPhotos = photosRef.current.map((p) =>
				p.imageId === imageItem.imageId
					? {
							...p,
							uploading: false,
							failed: true
						}
					: p
			)
			photosRef.current = nextPhotos
			onPhotosChange(nextPhotos)
			toast.error(t('luxe_submissions:photo_loading_error'))
		}
	}

	const uploadFiles = (images: ImageItem[], files: File[]) => {
		const pairs = images.map((img, i) => ({ img, file: files[i] }))
		let filesQueueIndex = 0
		const firstGroupSize = 6
		const othersGroupSize = 3
		let queue = [pairs.slice(0, firstGroupSize)]

		for (
			let index = firstGroupSize;
			index < pairs.length;
			index += othersGroupSize
		) {
			queue = queue.concat([pairs.slice(index, index + othersGroupSize)])
		}
		const filesQueue = queue
		const uploadFilesGroup = async (
			group: { img: ImageItem; file: File }[]
		) => {
			await Promise.all(
				group.map((item) => uploadFile(item.img, item.file))
			)

			const next = filesQueue[++filesQueueIndex]
			if (next) {
				uploadFilesGroup(next)
			}
		}

		uploadFilesGroup(filesQueue[0])
	}

	const handleUpload = async (files: File[]): Promise<void> => {
		if (isUploading.current) {
			return
		}
		isUploading.current = true
		setIsLoading(true)

		try {
			const galleryId = await ensureGallery()
			const { uploadUrls } = await httpRequestLuxeUploadPhotosLinks({
				galleryId,
				payload: { photoFileNames: files.map((f) => f.name) }
			})

			const matchedFiles = files.slice(0, uploadUrls.length)

			const pendingPhotos = uploadUrls.map((u, i) => ({
				galleryId,
				imageId: u.imageId,
				presignedUrl: u.preSignedUrl,
				imageUrl: URL.createObjectURL(matchedFiles[i]),
				uploading: true
			}))

			onPhotosChange([...photos, ...pendingPhotos])

			setIsLoading(false)
			isUploading.current = false

			uploadFiles(pendingPhotos, matchedFiles)
		} catch (error) {
			isUploading.current = false
			setIsLoading(false)
			errorHandler(error)
		}
	}

	const dropzoneState = useDropzone({
		disabled: isUploading.current,
		accept: {
			'image/jpeg': ['.jpeg'],
			'image/jpg': ['.jpg'],
			'image/png': ['.png']
		},
		maxFiles: MAX_FILES,
		multiple: true,
		onDropAccepted: (files) => {
			handleUpload(files)
		},
		onDropRejected: (rejectedFiles) => {
			if (rejectedFiles.length > MAX_FILES) {
				toast.error(
					t('errors:dropzone_max_file', {
						count: MAX_FILES
					})
				)
			}
		}
	})

	return (
		<Dropzone
			className={cn(
				className,
				dropzoneState.isDragActive
					? 'border-neutral-900 bg-neutral-200'
					: 'hover:border-neutral-900',
				isUploading.current &&
					'cursor-not-allowed border-neutral-300 bg-inherit hover:border-neutral-300'
			)}
			state={dropzoneState}
		>
			{children}
		</Dropzone>
	)
}
